import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { FileText, CheckCircle } from 'phosphor-react';

import {
  AppFile,
  canUserAccessBuyerBranch,
  collectBranchesFromBuyers,
  DillDispute,
} from '@dill/dill-shared';
import { twMerge } from 'tailwind-merge';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useAppSelector } from '../../../../redux/store';
import { DillColumn, DillTable } from '../../../general/Dill/DillTable';
import { DillButton, DillDropDownFilter, DillCheckbox } from '../../../general/Dill';

interface SupplierDisputesTableProps {
  disputes: DillDispute[];
  onMarkResolved: (disputeId: string) => void;
  onRowClick: (dispute: DillDispute) => void;
  hideCustomerColumn?: boolean;
  height?: string;
  toolbarRight?: React.ReactNode;
}

type DisputeTableRow = DillDispute & {
  customerName: string;
  statusLabel: DisputeStatusLabel;
};

type DisputeStatusLabel = 'Pending' | 'Resolved';

const createCheckboxItem = (label: string, value: string, isChecked: boolean) => ({
  label,
  value,
  labelInElement: (
    <div className="flex mx-1 my-1 items-center gap-2">
      <DillCheckbox isChecked={isChecked} />
      <div className="text-black text-xs">{label}</div>
    </div>
  ),
});

function SupplierDisputesTable({
  disputes,
  onMarkResolved,
  onRowClick,
  hideCustomerColumn,
  height,
  toolbarRight,
}: SupplierDisputesTableProps) {
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);

  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] } | null>(null);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: '',
    end: '',
  });

  const allTableData = useMemo<DisputeTableRow[]>(
    () =>
      disputes
        .map((dispute) => ({
          ...dispute,
          customerName: dispute.buyer?.name || '-',
          statusLabel: (dispute.isResolved ? 'Resolved' : 'Pending') as DisputeStatusLabel,
        }))
        .sort((a, b) => {
          if (a.isResolved !== b.isResolved) return a.isResolved ? 1 : -1;

          const aCreatedAt = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bCreatedAt = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return bCreatedAt - aCreatedAt;
        }),
    [disputes]
  );

  const tableData = useMemo(() => {
    let rows = allTableData;

    if (selectedFilters) {
      rows = rows.filter((row) => {
        const typeMatch =
          !selectedFilters.disputeType?.length ||
          selectedFilters.disputeType.includes(row.disputeType || 'OTHER');
        const statusMatch =
          !selectedFilters.statusLabel?.length ||
          selectedFilters.statusLabel.includes(row.statusLabel);
        const customerMatch =
          !selectedFilters.customerName?.length ||
          selectedFilters.customerName.includes(row.customerName);
        const sourceMatch =
          !selectedFilters.createdBySource?.length ||
          selectedFilters.createdBySource.includes(row.createdBySource || 'BUYER_SHORTPAY');
        const branchMatch =
          !selectedFilters.branch?.length ||
          selectedFilters.branch.includes(row.buyer?.branch ?? '');
        return typeMatch && statusMatch && customerMatch && sourceMatch && branchMatch;
      });
    }

    if (dateRange.start) {
      rows = rows.filter(
        (row) => row.createdAt && new Date(row.createdAt) >= new Date(dateRange.start)
      );
    }
    if (dateRange.end) {
      rows = rows.filter(
        (row) => row.createdAt && new Date(row.createdAt) <= new Date(`${dateRange.end}T23:59:59`)
      );
    }

    return rows;
  }, [allTableData, selectedFilters, dateRange]);

  const disputeTypeOptions = useMemo(
    () => Array.from(new Set(allTableData.map((row) => row.disputeType || 'OTHER'))).sort(),
    [allTableData]
  );

  const statusOptions = useMemo(
    () => Array.from(new Set(allTableData.map((row) => row.statusLabel))).sort(),
    [allTableData]
  );

  const customerOptions = useMemo(
    () => Array.from(new Set(allTableData.map((row) => row.customerName))).sort(),
    [allTableData]
  );

  const sourceOptions = useMemo(
    () => Array.from(new Set(allTableData.map((row) => row.createdBySource || 'BUYER_SHORTPAY'))),
    [allTableData]
  );

  // Branch lives on the customer, not the dispute - anything the user can't see is already
  // absent from `disputes` (the backend scopes it), so this list is inherently branch-limited.
  const filterableBranches = useMemo(
    () =>
      collectBranchesFromBuyers(
        allTableData
          .map((row) => row.buyer)
          .filter((buyer) =>
            canUserAccessBuyerBranch({
              user,
              supplierId: currentSupplier?.id,
              buyerBranch: buyer?.branch,
            })
          )
      ),
    [allTableData, user, currentSupplier?.id]
  );

  const dateRangeActiveLabel = useMemo(() => {
    if (!dateRange.start && !dateRange.end) return undefined;
    const fmt = (d: string) => (d ? moment(d).format('MM/DD/YYYY') : '...');
    return `Created: ${fmt(dateRange.start)} – ${fmt(dateRange.end)}`;
  }, [dateRange]);

  const filterItems = useMemo(
    () => [
      ...(!hideCustomerColumn && filterableBranches.length > 1
        ? [
            {
              id: 'branch',
              label: 'Filter by Branch',
              value: filterableBranches.map((branch) =>
                createCheckboxItem(
                  branch,
                  branch,
                  selectedFilters?.branch?.includes(branch) ?? false
                )
              ),
            },
          ]
        : []),
      {
        id: 'disputeType',
        label: 'Filter by Type',
        value: disputeTypeOptions.map((type) =>
          createCheckboxItem(type, type, selectedFilters?.disputeType?.includes(type) ?? false)
        ),
      },
      {
        id: 'statusLabel',
        label: 'Filter by Status',
        value: statusOptions.map((status) =>
          createCheckboxItem(
            status,
            status,
            selectedFilters?.statusLabel?.includes(status) ?? false
          )
        ),
      },
      {
        id: 'customerName',
        label: 'Filter by Customer',
        value: customerOptions.map((customer) =>
          createCheckboxItem(
            customer,
            customer,
            selectedFilters?.customerName?.includes(customer) ?? false
          )
        ),
      },
      {
        id: 'createdBySource',
        label: 'Filter by Source',
        value: sourceOptions.map((source) =>
          createCheckboxItem(
            source === 'SUPPLIER_MANUAL' ? 'Manual' : 'Customer',
            source,
            selectedFilters?.createdBySource?.includes(source) ?? false
          )
        ),
      },
      {
        id: 'createdAt',
        label: 'Filter by Date',
        value: [],
        activeLabel: dateRangeActiveLabel,
        customContent: (
          <div className="flex flex-col gap-2 pt-1">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-8">From</span>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))}
                className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-8">To</span>
              <input
                type="date"
                value={dateRange.end}
                min={dateRange.start || undefined}
                onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))}
                className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
            {(dateRange.start || dateRange.end) && (
              <button
                onClick={() => setDateRange({ start: '', end: '' })}
                className="text-xs text-gray-400 hover:text-gray-600 text-left mt-0.5">
                Clear dates
              </button>
            )}
          </div>
        ),
      },
    ],
    [
      selectedFilters,
      disputeTypeOptions,
      statusOptions,
      customerOptions,
      sourceOptions,
      dateRange,
      dateRangeActiveLabel,
      filterableBranches,
      hideCustomerColumn,
    ]
  );

  const handleFilterChange = (data: Record<string, { value: string }[]>) => {
    const transformed = Object.fromEntries(
      Object.entries(data).map(([key, arr]) => [key, arr.map((item) => item.value)])
    );
    setSelectedFilters(transformed);
  };

  const columns = useMemo<DillColumn<DisputeTableRow>[]>(
    () => [
      {
        key: 'createdAt',
        label: 'Created',
        width: 120,
        sortable: true,
        filterable: true,
        filterType: 'dateRange',
        getSortValue: (row) => (row.createdAt ? new Date(row.createdAt).getTime() : 0),
        render: (_: any, row: DillDispute) => (
          <p className="text-xs">
            {row.createdAt ? moment(row.createdAt).format('MM/DD/YYYY') : '-'}
          </p>
        ),
      },
      {
        key: 'customer',
        label: 'Customer',
        width: 180,
        sortable: true,
        filterable: true,
        filterKey: 'customerName',
        getSortValue: (row) => row.customerName,
        render: (_: any, row: DillDispute) => (
          <p className="text-xs">{row.buyer ? row?.buyer.name : '-'}</p>
        ),
      },
      {
        key: 'disputeType',
        label: 'Type',
        width: 120,
        sortable: true,
        filterable: true,
        getSortValue: (row) => row.disputeType ?? '',
        render: (_: any, row: DillDispute) => (
          <span
            className={twMerge(
              'px-2 py-1 rounded-full text-xs font-medium',
              row.disputeType === 'PAYMENT'
                ? 'bg-blue-100 text-blue-800'
                : row.disputeType === 'INVOICE'
                ? 'bg-purple-100 text-purple-800'
                : 'bg-gray-100 text-gray-800'
            )}>
            {row.disputeType || 'OTHER'}
          </span>
        ),
      },
      {
        key: 'createdBySource',
        label: 'Source',
        width: 110,
        sortable: true,
        filterable: true,
        getSortValue: (row) => row.createdBySource ?? '',
        render: (_: any, row: DillDispute) => (
          <span
            className={twMerge(
              'px-2 py-1 rounded-full text-xs font-medium',
              row.createdBySource === 'SUPPLIER_MANUAL'
                ? 'bg-indigo-100 text-indigo-800'
                : 'bg-gray-100 text-gray-800'
            )}>
            {row.createdBySource === 'SUPPLIER_MANUAL' ? 'Manual' : 'Customer'}
          </span>
        ),
      },
      {
        key: 'disputeReason',
        label: 'Reason',
        width: 200,
        sortable: true,
        filterable: true,
        getSortValue: (row) => row.disputeReason ?? '',
        render: (_: any, row: DillDispute) => (
          <p className="text-xs text-left w-full truncate" title={row.disputeReason || '-'}>
            {row.disputeReason || '-'}
          </p>
        ),
      },
      {
        key: 'disputeNotes',
        label: 'Notes',
        width: 220,
        sortable: true,
        getSortValue: (row) => row.disputeNotes ?? '',
        render: (_: any, row: DillDispute) => (
          <p className="text-xs text-left w-full truncate" title={row.disputeNotes || '-'}>
            {row.disputeNotes || '-'}
          </p>
        ),
      },
      {
        key: 'attachments',
        label: 'Attachments',
        width: 120,
        sortable: false,
        render: (_: any, row: DillDispute) =>
          row.disputeAttachments && row.disputeAttachments.length > 0 ? (
            <div className="flex items-center">
              <FileText
                size={18}
                weight="fill"
                className="text-blue-500 cursor-pointer"
                data-tooltip-id={`attachments-tooltip-${row.id}`}
              />
              <span className="ml-1 text-xs text-gray-600">({row.disputeAttachments.length})</span>
              <ReactTooltip
                id={`attachments-tooltip-${row.id}`}
                place="top"
                variant="dark"
                opacity="100%"
                style={{ zIndex: 10000 }}>
                <div className="text-wrap">
                  {row.disputeAttachments.map((file: AppFile, idx: number) => (
                    <div key={idx} className="mb-1">
                      <a
                        href={file.url || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-300 hover:underline">
                        {file.name || `Attachment ${idx + 1}`}
                      </a>
                    </div>
                  ))}
                </div>
              </ReactTooltip>
            </div>
          ) : (
            <span className="text-xs text-gray-400">None</span>
          ),
      },
      {
        key: 'statusLabel',
        label: 'Status',
        width: 120,
        sortable: true,
        filterable: true,
        getSortValue: (row) => row.statusLabel,
        render: (_: any, row: DillDispute) => (
          <span
            className={twMerge(
              'px-2 py-1 rounded-full text-xs font-medium',
              row.isResolved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
            )}>
            {row.isResolved ? 'Resolved' : 'Pending'}
          </span>
        ),
      },
      {
        key: 'actions',
        label: '',
        width: 130,
        sortable: false,
        render: (_: any, row: DillDispute) => (
          <div onClick={(e) => e.stopPropagation()}>
            {!row.isResolved ? (
              <DillButton
                label="Resolve"
                icon={<CheckCircle size={14} />}
                onClick={() => onMarkResolved(row.id)}
              />
            ) : (
              <span className="text-xs text-gray-400">-</span>
            )}
          </div>
        ),
      },
    ],
    [onMarkResolved]
  );

  return (
    <DillTable
      columns={hideCustomerColumn ? columns.filter((col) => col.key !== 'customer') : columns}
      data={tableData}
      sortable
      onRowClick={(row) => onRowClick(row)}
      rowHeight={60}
      emptyMessage="No disputes."
      countLabel="Disputes"
      showCount={false}
      striped={false}
      resizable={true}
      maxHeight={height}
      defaultSort={{ key: 'statusLabel', dir: 'asc' }}
      showActiveFilters
      toolbarLeft={
        <div className="w-[260px]">
          <DillDropDownFilter
            placeholder="Filters"
            closeMenu={false}
            isMulti
            showSelectedItems
            multiValue={selectedFilters}
            items={filterItems}
            showMinimalDesign
            onMultiSelectChange={handleFilterChange}
            inputHeight={30}
            numberOfItemsToShow={2}
          />
        </div>
      }
      toolbarRight={toolbarRight}
    />
  );
}

export default SupplierDisputesTable;
